import { DecimalPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Injector, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Router } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngrx/store';
import { isArray } from 'chart.js/helpers';
import moment from 'moment/moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, shareReplay, skip, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ITaskBoardSort } from 'src/app/dave-tasks/components/tasks-board/tasks-board.component';
import { BookmarkEntity } from '../../../../dave-data-module/entities/bookmark.entity';
import { EmployeeToVacationEntity, VacationStatusEnum, VacationTypeEnum, VacationTypeEnumNameMap } from '../../../../dave-data-module/entities/employee-to-vacation.entity';
import { EmployeeEntity } from '../../../../dave-data-module/entities/employee.entity';
import { EventTypeNamesEnum } from '../../../../dave-data-module/entities/event-type.entity';
import { EventEntity } from '../../../../dave-data-module/entities/event.entity';
import { EmployeeToVacationResolver } from '../../../../dave-data-module/guards/employee-to-vacation.resolver';
import { EmployeeResolver } from '../../../../dave-data-module/guards/employee.resolver';
import { StatusFromBackofficeResolver } from '../../../../dave-data-module/guards/statusFromBackoffice.resolver';
import { State } from '../../../../dave-data-module/State';
import { BookmarkActionTypes } from '../../../../dave-data-module/State/actions/bookmarks.actions';
import { EmployeeToVacationActionTypes } from '../../../../dave-data-module/State/actions/employee-to-vacation.actions';
import { EventsActionTypes } from '../../../../dave-data-module/State/actions/events.actions';
import { getBookmarkFetched, getBookmarks, getUnseenBookmarks } from '../../../../dave-data-module/State/selectors/bookmarks.selectors';
import { getEmployeeDictionary, getEmployees } from '../../../../dave-data-module/State/selectors/employees.selectors';
import { getEventTypes } from '../../../../dave-data-module/State/selectors/event-type.selector';
import { getUser, getUsers } from '../../../../dave-data-module/State/selectors/users.selectors';
import { NewVacationComponent, NewVacationComponentDialogData } from '../../../../dave-employee-administration-module/components/vacation/new-vacation/new-vacation.component';
import { EventListEntryComponent } from '../../../../dave-event-list-entry/components/event-list-entry/event-list-entry.component';
import { TaskFilterService } from '../../../../dave-tasks/services/task-filter.service';
import { clacEventFilterAmount, filterEventsByCategories,
    filterSingleEventByCategories, getEventFilterValues, IEventFilterType } from '../../../../helper/event.helper';
import { isNotNullOrUndefined } from '../../../../helper/helper';
import { AbsentMetaIcon, BookmarkIcon, TaskPageMeta, VacationMetaIcon } from '../../../../helper/page-metadata';
import { DefaultFilterService, FilterApps, FilterTypes } from '../../../../services/default-filter.service';
import { AppDialogService } from '../../../app-dialog-module/app-dialog.service';
import { FilterCardComponent } from '../../../app-filter-module/filter-card/filter-card.component';
import { BookmarkService } from '../../services/bookmark.service';
import { PermissionService } from '../../services/permission.service';
import { Permission } from '../../../../helper/permission.helper';
import { IFilterTypeSearchMultiSelectValue } from '../../../app-filter-module/app-filter/app-filter.component';
import { getPersons } from 'src/app/dave-data-module/State/selectors/person.selectors';
import { CustomLabelService } from '../../../../services/custom-label/custom-label.service';
import { selectEventCategories } from '../../../../dave-data-module/State/selectors/event-Categories.selector';
import { EventResolver } from '../../../../dave-data-module/guards/event.resolver';
import {LedgerImportEntity} from "../../../../dave-data-module/entities/ledger-import.entity";
import {LedgerImportDataService} from "../../../../dave-data-module/services/ledger-import-data.service";

interface TabData {
    icon: IconProp;
    searchLabel: string;
    headerButtons?: {
        checkAction?: () => void;
        checkLabel?: string;
        checkIcon?: IconProp;
        disableCheck$?: Observable<boolean>;
        matMenu?: ElementRef<any>;
        matBadge$?: Observable<string | number | undefined | null>;
    }[];
}

interface VacationTableData {
    vacation: EmployeeToVacationEntity;
    employee: EmployeeEntity;
    acceptedList: string[];
    declinedList: string[];
    disableApprove: boolean;
    disableDecline: boolean;
}

@Component({
    selector: 'app-history-bookmarks',
    templateUrl: './history-bookmarks.component.html',
    styleUrls: ['./history-bookmarks.component.scss'],
    providers: [DecimalPipe],
})
export class HistoryBookmarksComponent implements OnDestroy, AfterViewInit {
    @Input() HeaderButtonInverted = true;

    public TaskPageMeta = TaskPageMeta;
    public BookmarkIcon = BookmarkIcon;
    public VacationMetaIcon = VacationMetaIcon;
    public AbsentMetaIcon = AbsentMetaIcon;

    public VacationTypeEnumNameMap = VacationTypeEnumNameMap;
    // Searchbar
    public SearchBarActive = false;
    public SearchForm = new FormControl<string>('');
    public IsSearching = false;

    private event2Categories$ = this.store.select(selectEventCategories).pipe(
        map(ec => ec ? ec.Event2EventCategories : []),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    /** Daten der Tabelle */
    public AbsentDataSource$: Observable<MatTableDataSource<VacationTableData>>;
    private bookmarks$ = this.store.select(getBookmarkFetched).pipe(
        filter((v) => !!v),
        switchMap(() => this.store.select(getBookmarks)),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    public TaskSort$: BehaviorSubject<ITaskBoardSort> = new BehaviorSubject({ sortBy: 'endDate', direction: 'dsc' });
    public DataSource$: Observable<EventEntity[]> = combineLatest([
        this.bookmarks$,
        this.store.select(getEventTypes).pipe(filter(isNotNullOrUndefined)),
        this.store.select(getUsers).pipe(filter(isNotNullOrUndefined)),
        this.store.select(getPersons).pipe(filter(isNotNullOrUndefined)),
    ]).pipe(
        map(([bookmarks, eventTypes, users, persons]) =>
            bookmarks
                .map((bookmark) => bookmark.Event)
                // .filter((value) => !this.personId || value.PersonId === this.personId)
                .sort((a, b) => {
                    const aDate = +a.EventDate.getTime();
                    const bDate = +b.EventDate.getTime();
                    return bDate - aDate;
                })
                .map((task) => {
                    const userNames = task.UserIds?.map((u) => users.find((p) => p.Id === u)?.DisplayName)
                        .filter((un) => un !== '')
                        .join('; ');
                    const eventType = eventTypes.find((event) => event.Id === task.EventTypeId);
                    const eventPerson = persons.find((person) => person.Id === task.PersonId);
                    return {
                        userNames: userNames ? userNames : '', // eMail?.From,
                        EventType: eventType,
                        EventPerson: eventPerson ? eventPerson : null,
                        Event: task,
                    };
                }),
        ),
        // tap((v) => this.UnseenBookmarkCount$.next(v.filter((e) => !e.Event.LastSeenAt || e.Event.LastSeenAt < e.Event.UpdatedAt).length)),
        switchMap((bookmarks) =>
            this.SearchForm.valueChanges.pipe(
                startWith(''),
                map(() => (this.SearchForm.value || '').trim().toLowerCase()),
                map((searchTerm) =>
                    (searchTerm ? bookmarks
                        .filter((data) => [
                            data.Event?.From,
                            ...(data.Event?.To || []),
                            data.userNames, data.Event?.Hint || '',
                            data.Event?.Description || '',
                            data.Event?.Name || '',
                            data.EventPerson?.Firstname || '',
                            data.EventPerson?.Lastname || '',
                            data.EventPerson?.Firstname + ' ' + data.EventPerson?.Lastname || '',
                            data.EventPerson?.Lastname + ' ' + data.EventPerson?.Firstname || '',
                        ]
                        .map((value) => value?.trim().toLowerCase())
                        .some((value) => value?.includes(searchTerm)))
                    : bookmarks
                    ).map((data) => data.Event)
                ),
            ),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    public UnseenBookmarkCount$ = this.store.select(getUnseenBookmarks).pipe(map((b) => b.length));
    public UnseenTaskCount$ = this.bookmarkService.UnseenTasksCount$;
    public VacationDataSource$: Observable<MatTableDataSource<VacationTableData>>;
    public Bookmarks$: Observable<BookmarkEntity[]>;
    private onDestroy$ = new Subject<void>();
    public TaskDataSource$: Observable<number[]>;
    public EventLedgerImports$: Observable<LedgerImportEntity[]>;
    /** Der `MatSort` der Tabelle */
    @ViewChild(MatSort) private matSort?: MatSort;

    /** Das Suchfeld-Element */
    @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;
    @ViewChild('sortMenu') private menuRef?: ElementRef<any>;
    @ViewChild('taskFilterComponent') private taskFilterComponent?: FilterCardComponent;

    public Loaded$: Observable<boolean>;
    public EventTypesEnum = EventTypeNamesEnum;
    public Tabs: TabData[];
    public SelectedTab: TabData;
    public TaskFilterSettings$ = this.taskFilterService.filterSettings$;
    public TaskFilterValues$: BehaviorSubject<IEventFilterType> = getEventFilterValues();
    protected TaskFilterAmount$ = this.TaskFilterValues$.pipe(map(clacEventFilterAmount), shareReplay({ refCount: true, bufferSize: 1 }));

    constructor(
        private taskFilterService: TaskFilterService,
        private store: Store<State>,
        protected router: Router,
        public cls: CustomLabelService,
        private dialog: MatDialog,
        public PS: PermissionService,
        public appDialog: AppDialogService,
        statusFromBackofficeResolver: StatusFromBackofficeResolver,
        employeeToVacationsResolver: EmployeeToVacationResolver,
        employeesResolver: EmployeeResolver,
        private injector: Injector,
        protected bookmarkService: BookmarkService,
        private defaultFilterService: DefaultFilterService,
        protected ledgerImportDataService: LedgerImportDataService
    ) {
        this.TaskFilterValues$.pipe(takeUntil(this.onDestroy$), skip(2)).subscribe((val) => {
            if (Object.keys(val).length !== 0) {
                this.defaultFilterService.SetFilterByApp(FilterApps.HistoryBookmarkComponentTasks, val);
            }
        });
        combineLatest([this.TaskFilterSettings$, this.defaultFilterService.GetFilterByApp$(FilterApps.HistoryBookmarkComponentTasks)])
            .pipe(take(1))
            .subscribe(([val, filterValues]) => {
                this.TaskFilterValues$.next({
                    [FilterTypes.Priority]: isArray(filterValues[FilterTypes.Priority]) ? filterValues[FilterTypes.Priority] as IFilterTypeSearchMultiSelectValue[] : null,
                    [FilterTypes.Date]:
                        filterValues[FilterTypes.Date]
                            ? { from: filterValues[FilterTypes.Date].from && moment(filterValues[FilterTypes.Date].from), to: filterValues[FilterTypes.Date].to && moment(filterValues[FilterTypes.Date].to) }
                            : null,
                    [FilterTypes.CommissionId]: isArray(filterValues[FilterTypes.CommissionId]) ? filterValues[FilterTypes.CommissionId] as IFilterTypeSearchMultiSelectValue[] : null,
                    [FilterTypes.UserId]: isArray(filterValues[FilterTypes.UserId]) ? filterValues[FilterTypes.UserId] as IFilterTypeSearchMultiSelectValue[] : null,
                    [FilterTypes.AutorId]: isArray(filterValues[FilterTypes.AutorId]) ? filterValues[FilterTypes.AutorId] as IFilterTypeSearchMultiSelectValue[] : null,
                    [FilterTypes.EndDate]: filterValues[FilterTypes.EndDate]
                        ? { from: filterValues[FilterTypes.EndDate].from && moment(filterValues[FilterTypes.EndDate].from), to: filterValues[FilterTypes.EndDate].to && moment(filterValues[FilterTypes.EndDate].to) }
                        : null,
                    [FilterTypes.EventCategory]: null,
                    [FilterTypes.Persons]: null,
                    [FilterTypes.EventType]: null,
                    [FilterTypes.Milestones]: null,
                });
            });
        this.Bookmarks$ = this.store.select(getBookmarks);
        // müssen hier resolved werden da das BE kaputt geht wenn man sie abruft, ohne die Berechtigung dafür zu haben
        this.Loaded$ = combineLatest([
            ...Object.values(EventListEntryComponent.RequiredResolvers).filter((r) => r !== EventResolver).map((r) => this.injector.get(r).resolve()),
            statusFromBackofficeResolver.resolve(),
            employeeToVacationsResolver.resolve(),
            employeesResolver.resolve(),
        ]).pipe(
            take(1),
            map(() => true),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

        this.TaskDataSource$ = combineLatest([
            this.bookmarkService.Tasks$.pipe(
                switchMap((tasks) =>
                    combineLatest([this.TaskFilterValues$, this.event2Categories$]).pipe(
                        map(([filter, e2cs]) => {
                            console.log({ filter });
                            return tasks.filter((t) => {
                                if (filter[FilterTypes.Date]?.from && (!t.EventDate || filter[FilterTypes.Date].from.isAfter(t.EventDate))) {
                                    return false;
                                }
                                if (filter[FilterTypes.Date]?.to && (!t.EventDate || filter[FilterTypes.Date].to.isBefore(t.EventDate))) {
                                    return false;
                                }
                                if (filter[FilterTypes.EndDate]?.from && (!t.EventEndDate || filter[FilterTypes.EndDate].from.isAfter(t.EventEndDate))) {
                                    return false;
                                }
                                if (filter[FilterTypes.EndDate]?.to && (!t.EventEndDate || filter[FilterTypes.EndDate].to.isBefore(t.EventEndDate))) {
                                    return false;
                                }
                                if (filter[FilterTypes.AutorId]?.length && !filter[FilterTypes.AutorId].some((e) => t.UserId && e.id === t.UserId)) {
                                    return false;
                                }
                                if (filter[FilterTypes.CommissionId]?.length && !filter[FilterTypes.CommissionId].some((e) => e.id === t.CommissionId)) {
                                    return false;
                                }
                                if (filter[FilterTypes.UserId]?.length && !filter[FilterTypes.UserId].some((e) => t.UserIds.includes(+e.id))) {
                                    return false;
                                }
                                if (filter[FilterTypes.Priority]?.length && !filter[FilterTypes.Priority].some((e) => e.id === t.AdditionalData.Priority)) {
                                    return false;
                                }
                                if (filter[FilterTypes.EventCategory]?.length) {
                                    const categoryIds = filter[FilterTypes.EventCategory].map(v => +v.id);
                                    if (!filterSingleEventByCategories(t, e2cs, categoryIds)) {
                                        return false;
                                    }
                                }
                                return true;
                            });
                        }),
                    )
                ),
            ),
            this.store.select(getUsers).pipe(filter(isNotNullOrUndefined)),
        ]).pipe(
            map(([tasks, users]) =>
                tasks
                    // .filter((value) => !this.personId || value.PersonId === this.personId)
                    .sort((a, b) => {
                        const aDate = a.EventEndDate;
                        const bDate = b.EventEndDate;
                        if (aDate === null && bDate === null) {
                            return 0;
                        }
                        if (aDate === null && bDate !== null) {
                            return 1;
                        }
                        if (aDate !== null && bDate === null) {
                            return -1;
                        }
                        return aDate.getTime() - bDate.getTime();
                    })
                    .map((task) => {
                        const userNames = task.UserIds?.map((u) => users.find((p) => p.Id === u)?.DisplayName)
                            .filter((un) => un !== '')
                            .join('; ');

                        return {
                            userNames: userNames ? userNames : '', // eMail?.From,
                            Event: task,
                        };
                    }),
            ),
            // tap((v) => this.UnseenTaskCount$.next(v.filter((e) => !e.Event.LastSeenAt || e.Event.LastSeenAt < e.Event.UpdatedAt).length)),
            switchMap((tasks) =>
                this.TaskSort$.pipe(
                    distinctUntilChanged((a, b) => a.sortBy === b.sortBy && a.direction === b.direction),
                    map((sort) => {
                        switch (sort.sortBy) {
                            case 'endDate':
                                return sort.direction === 'dsc' ? tasks : tasks.slice().reverse();
                            case 'priority':
                                const sortTask = tasks.slice().sort((a, b) => {
                                    if (a.Event.AdditionalData?.Priority === b.Event.AdditionalData?.Priority) {
                                        return 0;
                                    }
                                    if (!a.Event.AdditionalData?.Priority) {
                                        return -1;
                                    }
                                    if (!b.Event.AdditionalData?.Priority) {
                                        return 1;
                                    }
                                    return a.Event.AdditionalData.Priority - b.Event.AdditionalData.Priority;
                                });
                                return sort.direction === 'asc' ? sortTask : sortTask.reverse();
                        }
                    }),
                ),
            ),
            switchMap((tasks) =>
                this.SearchForm.valueChanges.pipe(
                    startWith(''),
                    map(() => (this.SearchForm.value || '').trim().toLowerCase()),
                    map((searchTerm) =>
                        (searchTerm
                            ? tasks.filter((data) => [data.userNames, data.Event?.Hint || '', data.Event?.Description || '', data.Event?.Name || '']
                                .map((value) => value?.trim().toLowerCase())
                                .some((value) => value?.includes(searchTerm)))
                            : tasks
                        ).map((data) => data.Event.Id),
                    ),
                ),
            ),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

        this.EventLedgerImports$ = this.TaskDataSource$.pipe(
            switchMap(ids => ids.length > 0 ? this.ledgerImportDataService.getLedgerImportByEventIds$(ids) : of([] as LedgerImportEntity[])),
            shareReplay({ refCount: true, bufferSize: 1 }),
        )

        this.VacationDataSource$ = combineLatest([this.store.select(getEmployeeDictionary).pipe(filter(isNotNullOrUndefined)), this.bookmarkService.getVacations(), this.store.select(getUser)]).pipe(
            map(([employees, e2vAbsent, user]) => {
                return new MatTableDataSource<VacationTableData>(
                    e2vAbsent.map((e2v) => {
                        const employee: EmployeeEntity = employees[e2v.EmployeeId];
                        const acceptedList = e2v.UserIdListApproved?.map((ua) => {
                            const e = Object.values(employees).find((e) => e.UserId === ua);
                            return e ? e.Firstname + ' ' + e.Lastname : '';
                        });
                        const declinedList =
                            e2v.Type === VacationTypeEnum.Vacation
                                ? (e2v.UserIdListDeclined || []).map((ua) => {
                                      const e = Object.values(employees).find((e) => e.UserId === ua);
                                      return e ? e.Firstname + ' ' + e.Lastname : '';
                                  })
                                : null;
                        return {
                            vacation: e2v,
                            employee,
                            acceptedList,
                            declinedList,
                            disableApprove: e2v.UserIdListApproved?.includes(user?.Id) || e2v.VacationStatus === VacationStatusEnum.Approved,
                            disableDecline: e2v.UserIdListDeclined?.includes(user?.Id) || e2v.VacationStatus === VacationStatusEnum.Declined,
                        };
                    }),
                );
            }),
            tap((dataSource) => (dataSource.filterPredicate = (data, searchTerm) => [data.employee?.Firstname, data.employee?.Lastname].map((value) => value?.trim().toLowerCase()).some((value) => value?.includes(searchTerm)))),
            switchMap((dataSource) =>
                this.SearchForm.valueChanges.pipe(
                    startWith(''),
                    map(() => (this.SearchForm.value || '').trim().toLowerCase()),
                    tap((searchTerm) => (dataSource.filter = searchTerm)),
                    map(() => dataSource),
                ),
            ),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.AbsentDataSource$ = combineLatest([this.store.select(getEmployees).pipe(filter(isNotNullOrUndefined)), this.bookmarkService.getAbsents(), this.store.select(getUser)]).pipe(
            map(([employees, e2v, user]) => {
                return new MatTableDataSource<VacationTableData>(
                    e2v.map((e) => {
                        const acceptedList = e.UserIdListApproved?.map((ua) => {
                            const e = employees.find((e) => e.UserId === ua);
                            return e ? e.Firstname + ' ' + e.Lastname : '';
                        });
                        const declinedList = null;
                        return {
                            vacation: e,
                            employee: employees.find((em) => em.Id === e.EmployeeId),
                            acceptedList,
                            declinedList,
                            disableApprove: e.UserIdListApproved?.includes(user?.Id),
                            disableDecline: e.UserIdListDeclined?.includes(user?.Id),
                        };
                    }),
                );
            }),
            tap((dataSource) => (dataSource.filterPredicate = (data, searchTerm) => [data.employee?.Firstname, data.employee?.Lastname].map((value) => value?.trim().toLowerCase()).some((value) => value?.includes(searchTerm)))),
            switchMap((dataSource) =>
                this.SearchForm.valueChanges.pipe(
                    startWith(''),
                    map(() => (this.SearchForm.value || '').trim().toLowerCase()),
                    tap((searchTerm) => (dataSource.filter = searchTerm)),
                    map(() => dataSource),
                ),
            ),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

    }
    ngAfterViewInit(): void {
        this.Tabs = [
            {
                icon: TaskPageMeta.Icon,
                searchLabel: 'Meine Aufgaben durchsuchen',
                headerButtons: [
                    {
                        checkAction: () => this.taskFilterComponent?.Toggle(),
                        checkLabel: 'Filter',
                        checkIcon: 'filter',
                        matBadge$: this.TaskFilterAmount$.pipe(map((count) => count || null)),
                    },
                    {
                        checkLabel: 'Sortieren',
                        checkIcon: 'sort',
                        matMenu: this.menuRef,
                        checkAction: () => {},
                    },
                    {
                        checkAction: () => this.clearTasks(),
                        checkLabel: 'Alle Aufgaben gesehen',
                        checkIcon: 'eye',
                        disableCheck$: this.UnseenTaskCount$.pipe(map((t) => !t)),
                    },
                ],
            },
            {
                icon: 'bookmark',
                searchLabel: 'Meine Lesezeichen durchsuchen',
                headerButtons: [
                    {
                        checkAction: () => this.deleteBookmarks(),
                        checkLabel: 'Alle Lesezeichen entfernen',
                        checkIcon: 'check',
                        disableCheck$: this.DataSource$.pipe(map((d) => !d.length)),
                    },
                    {
                        checkAction: () => this.clearBookmarks(),
                        checkLabel: 'Alle Lesezeichen gesehen',
                        checkIcon: 'eye',
                        disableCheck$: this.DataSource$.pipe(map((d) => d.every((b) => b.LastSeenAt?.getTime() >= b.UpdatedAt.getTime()))),
                    },
                ],
            },
            {
                icon: VacationMetaIcon,
                searchLabel: 'Urlaubsanträge durchsuchen',
            },
            {
                icon: AbsentMetaIcon,
                searchLabel: 'Abwesenheiten durchsuchen',
            },
        ];
        this.SelectedTab = this.Tabs[0];
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }
    public deleteBookmarks() {
        this.bookmarks$.pipe(first()).subscribe((bookmarks) => this.store.dispatch(BookmarkActionTypes.RemoveBookmark({ BookmarkIds: bookmarks.map((d) => d.Id) })));
    }
    private clearTasks() {
        this.TaskDataSource$.pipe(first()).subscribe((tasks) => this.store.dispatch(EventsActionTypes.SetEventSeen({ Payload: { eventIds: tasks } })));
    }
    private clearBookmarks() {
        this.bookmarks$.pipe(first()).subscribe((bookmarks) => this.store.dispatch(EventsActionTypes.SetEventSeen({ Payload: { eventIds: bookmarks.map((b) => b.EventId) } })));
    }
    public OpenVacationDialog(id: number, approved: boolean) {
        this.appDialog.OpenVacationConfirmationDialog(id, approved).subscribe();
    }
    public SetAbsentToRead(id: number, approved: boolean) {
        this.store.dispatch(EmployeeToVacationActionTypes.Approve({ Payload: { id, approved } }));
    }
    public SearchButtonClicked() {
        if (!this.SearchBarActive) {
            this.SearchBarActive = true;
            setTimeout(() => this.inputRef.nativeElement.focus(), 30);
        } else {
            if (this.SearchForm.value.length === 0) {
                this.SearchBarActive = false;
            }
        }
    }
    public OpenVacationPopUp(entry: { vacation: EmployeeToVacationEntity } = null) {
        this.dialog.open<NewVacationComponent, NewVacationComponentDialogData>(NewVacationComponent, {
            ...NewVacationComponent.DefaultConfig,
            data: {
                employeeId: entry?.vacation.EmployeeId,
                employeeToVacationId: entry?.vacation.Id,
            },
        });
    }
}
